import { BellRing, BriefcaseBusiness, CheckCircle2, ListTodo, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const formatContextTime = (value) => {
  if (!value) return "Kontext gespeichert";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const localizeGeneratedCopy = (value) => {
  if (!value) return value;

  const exact = {
    "Review open tasks and assign the next action.": "Prüfe offene Aufgaben und lege den nächsten Schritt fest.",
    "0 active goals, 0 active projects, 0 open tasks, and 0 open commitments are currently tracked.": "Aktuell werden 0 aktive Ziele, 0 aktive Projekte, 0 offene Aufgaben und 0 offene Zusagen verfolgt.",
  };

  if (exact[value]) return exact[value];

  return value
    .replace("active goals", "aktive Ziele")
    .replace("active projects", "aktive Projekte")
    .replace("open tasks", "offene Aufgaben")
    .replace("open commitments", "offene Zusagen");
};

const renderList = (items, renderItem, emptyTitle, emptyText, testId) => {
  if (!items?.length) return <AionEmptyState title={emptyTitle} description={emptyText} testId={testId} />;
  return <div className="aion-list" data-testid={`${testId}-list`}>{items.map(renderItem)}</div>;
};

export const TodayView = ({ morningBrief, notifications, openTasks, openDecisions, recentMemories, suggestions, onArchiveNotification, onMarkNotificationRead, onOpenDecisions, onOpenNotifications, onOpenPersonalHealth, onRunMorningBrief, canRefreshMorningBrief = true }) => (
  <div className="aion-view aion-today-grid" data-testid="today-view">
    <AionSectionCard
      title="Heute"
      eyebrow="DEIN TAG"
      subtitle="Tagesbrief, offene Punkte, Entscheidungen, Erinnerungen und Vorschläge in einer klaren Ansicht."
      action={<Button onClick={onRunMorningBrief} disabled={!canRefreshMorningBrief} data-testid="today-refresh-brief-button">{canRefreshMorningBrief ? "Morning Brief aktualisieren" : "Nur im eigenen Profil"}</Button>}
      testId="today-hero-card"
    >
      <div className="aion-summary-banner" data-testid="today-summary-banner">{localizeGeneratedCopy(morningBrief?.summary) || "AION erstellt den Morning Brief, sobald echte Tagesdaten vorliegen."}</div>
    </AionSectionCard>

    <AionSectionCard title="Tagesbrief" eyebrow="BRIEF" subtitle="Bestehende Intelligence-API, ohne neues System." testId="today-morning-brief-card">
      {renderList(
        morningBrief?.top_priorities,
        (item, index) => <div className="aion-list-row" key={`${item}-${index}`} data-testid={`today-priority-${index}`}><BriefcaseBusiness className="aion-list-icon" aria-hidden="true" /><span>{localizeGeneratedCopy(item)}</span></div>,
        "Noch kein Morning Brief.",
        "AION zeigt hier automatisch den echten Brief des Tages, sobald er erzeugt wurde.",
        "today-morning-brief-empty"
      )}
    </AionSectionCard>

    <AionSectionCard title="Offene Punkte" eyebrow="OFFENE PUNKTE" subtitle="Aufgaben und offene Entscheidungen." testId="today-open-items-card">
      {renderList(
        [...(openTasks || []).map((item) => ({ label: item.title, meta: item.classification || "task" })), ...(openDecisions || []).map((item) => ({ label: item.title, meta: item.review_status || "decision" }))],
        (item, index) => <div className="aion-list-row" key={`${item.label}-${index}`} data-testid={`today-open-item-${index}`}><ListTodo className="aion-list-icon" aria-hidden="true" /><div><strong>{item.label}</strong><small>{item.meta === "task" ? "Aufgabe" : item.meta === "decision" ? "Entscheidung" : item.meta === "open" ? "Offen" : item.meta}</small></div></div>,
        "Keine offenen Punkte.",
        "Aktuell sind keine offenen Aufgaben oder Entscheidungs-Flags vorhanden.",
        "today-open-items-empty"
      )}
    </AionSectionCard>

    <AionSectionCard title="Entscheidungen" eyebrow="ENTSCHEIDUNGEN" subtitle="Echte Entscheidungs-Einträge aus dem bestehenden Intelligence-Layer." action={<Button size="sm" variant="outline" onClick={() => onOpenDecisions?.()} data-testid="today-open-decision-review">Decision Review</Button>} testId="today-decisions-card">
      {renderList(
        openDecisions,
        (item, index) => <div className="aion-list-row" key={item.id || index} data-testid={`today-decision-${index}`}><CheckCircle2 className="aion-list-icon" aria-hidden="true" /><div><strong>{item.title}</strong><small>{item.review_status === "open" ? "Offen" : item.review_status || "Offen"}</small></div></div>,
        "Keine offenen Entscheidungen.",
        "AION hat aktuell keine entscheidungsrelevanten Warnungen oder offenen Reviews.",
        "today-decisions-empty"
      )}
    </AionSectionCard>

    <AionSectionCard title="Erinnerungen" eyebrow="MEMORY" subtitle="Zuletzt gespeicherter Tageskontext aus dem Memory-System." testId="today-memories-card">
      {renderList(
        recentMemories,
        (item, index) => (
          <div className="aion-list-row" key={`${item.recorded_at || index}-${index}`} data-testid={`today-memory-${index}`}>
            <div className="aion-list-copy" data-testid={`today-memory-copy-${index}`}>
              <strong>{item.summary || item.title || item.type || "Erinnerung"}</strong>
              <small>{formatContextTime(item.recorded_at)}</small>
            </div>
          </div>
        ),
        "Noch keine Tages-Erinnerungen.",
        "Sobald AION echte Kontexte speichert, erscheinen sie hier.",
        "today-memories-empty"
      )}
    </AionSectionCard>

    <AionSectionCard
      title="Hinweise"
      eyebrow="UPDATES"
      subtitle="Reale Hinweise aus dem Personal Intelligence OS."
      action={<Button size="sm" variant="outline" onClick={() => onOpenNotifications?.()} data-testid="today-open-notifications-center">Alle Hinweise</Button>}
      testId="today-notifications-card"
    >
      {renderList(
        notifications,
        (item, index) => (
          <div className="aion-list-row aion-list-row-actions" key={item.id || index} data-testid={`today-notification-${index}`}>
            <div>
              <strong>{item.title}</strong>
              <small>{item.message}</small>
            </div>
            {(onOpenPersonalHealth || onMarkNotificationRead || onArchiveNotification) ? (
              <div className="aion-inline-actions" data-testid={`today-notification-actions-${index}`}>
                {item.payload?.action === "OPEN_PERSONAL_HEALTH" && onOpenPersonalHealth ? <Button size="sm" onClick={() => onOpenPersonalHealth()} data-testid={`today-notification-open-health-${index}`}>Health öffnen</Button> : null}
                {!item.read && onMarkNotificationRead ? <Button size="sm" variant="outline" onClick={() => onMarkNotificationRead(item.id)} data-testid={`today-notification-read-${index}`}>Gelesen</Button> : null}
                {onArchiveNotification ? <Button size="sm" variant="ghost" onClick={() => onArchiveNotification(item.id)} data-testid={`today-notification-archive-${index}`}>Archivieren</Button> : null}
              </div>
            ) : null}
          </div>
        ),
        "Keine Hinweise.",
        "AION hat aktuell keine neuen Hinweise für heute.",
        "today-notifications-empty"
      )}
    </AionSectionCard>

    <AionSectionCard title="Vorschläge" eyebrow="VORSCHLÄGE" subtitle="Bestehende Suggestions-Engine, ohne Fake-Daten." testId="today-suggestions-card">
      {renderList(
        suggestions,
        (item, index) => <div className="aion-list-row" key={item.id || index} data-testid={`today-suggestion-${index}`}><Sparkles className="aion-list-icon" aria-hidden="true" /><div><strong>{item.title}</strong><small>{item.message}</small></div></div>,
        "Noch keine Vorschläge.",
        "AION zeigt hier nur echte Vorschläge aus Verhalten, Energie, Presence und Memory.",
        "today-suggestions-empty"
      )}
    </AionSectionCard>
  </div>
);